import PortalIdParser from 'PortalIdParser';
import enviro from 'enviro';
import userInfo from 'hub-http/userInfo';
import { createTracker } from 'usage-tracker';
import {
  LockedTrackerInstance,
  TrackerConfig,
} from 'usage-tracker-core/trackerTypes';
import events from './events.yaml';
import { EventName, SubscriptionEvent } from './types';

const DEFAULT_TRACKER_PROPS: TrackerConfig = {
  trackerName: 'commerce-subscriptions-tracker',
  events,
  properties: {
    namespace: 'commerce-subscription',
    email: () =>
      userInfo({ cached: true, ignoreRedirect: true })
        .then(({ user }) => user.email)
        .catch(() => 'unknown'),
    hubId: () => PortalIdParser.get() ?? -1,
  },
  lastKnownEventProperties: ['app', 'paymentType'],
  debug: () => enviro.debug('commerce-subscription-lib') as boolean,
  preserveTrackerEvents: true,
  bypassPool: true,
};

const tracker = createTracker(DEFAULT_TRACKER_PROPS);

function track(
  app: string,
  eventName: EventName,
  values: Readonly<SubscriptionEvent | SubscriptionEvent[]>,
  internalTracker: LockedTrackerInstance<TrackerConfig> = tracker
) {
  if (values instanceof Array) {
    values.forEach((value) => {
      internalTracker.track(eventName, {
        ...value,
        app,
      });
    });
  } else {
    internalTracker.track(eventName, {
      ...values,
      app,
    });
  }
}

const createCustomTracker = (customTrackerConfig: Partial<TrackerConfig>) => {
  return createTracker({
    ...DEFAULT_TRACKER_PROPS,
    ...customTrackerConfig,
    properties: {
      ...DEFAULT_TRACKER_PROPS.properties,
      ...(customTrackerConfig.properties ?? {}),
    },
  });
};

export function getTracker(
  app:
    | 'card-profile-products-lib'
    | 'card-subscription-highlight-lib'
    | 'commerce-subscription-cards-lib'
    | 'payment-links-ui'
    | 'payments-ui-app'
    | 'quotes-admin-ui'
    | 'subscriptions-admin-ui'
    | (string & {}), // typescript trick to allow autocomplete of known strings
  customTrackerConfig?: Partial<TrackerConfig>
) {
  const customTracker = createCustomTracker(customTrackerConfig ?? {});
  return (
    eventName: EventName,
    values: Readonly<SubscriptionEvent | SubscriptionEvent[]>
  ) =>
    customTrackerConfig
      ? track(app, eventName, values, customTracker)
      : track(app, eventName, values);
}
